import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { PairingPayload } from "@foreman/protocol";

export type PairFailure = "rejected" | "offline" | "timeout" | "lost" | "reply";

export type View =
  /** The first screen: every session on every computer, what needs you first. A root (tab) screen, like inbox, computers and account. */
  | { name: "home" }
  | { name: "computers" }
  | { name: "scan" }
  | { name: "confirm"; payload: PairingPayload }
  /** An iPhone browser tab: add the app to the Home Screen first, and finish pairing there (its storage is separate from Safari's). */
  | { name: "installfirst"; payload: PairingPayload }
  | { name: "pairing"; payload: PairingPayload; phoneName: string; computerName: string }
  | { name: "sessions"; computerId: string; newSession?: boolean }
  | { name: "inbox" }
  /** The team of agents on a computer: give it a goal, watch the plan, open any agent. A root (tab) screen. */
  | { name: "teams" }
  | { name: "account" }
  /** How much of each agent's plan limits is used (from Settings). */
  | { name: "usage"; forComputer?: string }
  /** The approval rules kept on a computer (from Settings). */
  | { name: "rules"; forComputer?: string }
  /** The skill library kept on a computer, and which agents use each skill (from Settings). */
  | { name: "skills"; forComputer?: string; /** Start with skills turned on for this folder only (the session's folder). */ folder?: string }
  | { name: "devices"; computerId: string }
  /** Which link of phone, internet, relay and computer is broken. */
  | { name: "diagnostics"; computerId: string }
  /** Report a problem to Brocoded; `diag` is the connection check text to attach if the person agrees. */
  | { name: "support"; topic?: string; diag?: string }
  | { name: "terminal"; computerId: string; sessionId: string }
  | { name: "changes"; computerId: string; sessionId: string; against?: string }
  | { name: "branches"; computerId: string; sessionId: string }
  | { name: "history"; computerId: string; sessionId: string }
  | { name: "earlier"; computerId: string; sessionId: string }
  /** Offered while connecting a computer; `then` is where to go once signed in or skipped. */
  | { name: "signin"; then: View; required?: boolean }
  /** Signing in: the page a computer sends you to (`/?link=ABCD-2345`). */
  | { name: "link"; code: string };

export interface Nav {
  view: View;
  /** Go forward; the browser/OS back gesture comes back here. */
  push(view: View): void;
  /** Swap the current screen without adding a history entry. */
  replace(view: View): void;
  back(): void;
  /** True on a root (tab) screen: nothing to go back to, so the tab bar shows instead of a back button. */
  isRoot: boolean;
  /** Switch tabs: make `view` the root screen, dropping everything above the old one. */
  goTab(view: View): void;
  /** Return to the first screen, dropping everything above it. */
  home(): void;
  /** Return to the first screen and open `view` on top of it (e.g. a computer right after pairing). */
  resetTo(view: View): void;
}

const ROOT: View = { name: "home" };

/**
 * A tiny stack navigator that also drives the browser history, so the Android back button and the
 * iOS back swipe move through screens instead of leaving the app. The stack lives in a ref (history
 * calls are side effects and must not run inside state updaters, which React may invoke twice).
 */
export function useNav(initial: View = ROOT): Nav {
  const stack = useRef<View[]>([initial]);
  /** Screens we went back from, so the browser's Forward button can bring them back. */
  const forward = useRef<View[]>([]);
  // When a reset started (0: none). A reset waits for the browser's popstate; if that never comes (the history was shorter than
  // our stack, e.g. after a reload) a plain flag would stay set forever and every Back/Home would do nothing. After a couple of
  // seconds a reset is treated as over.
  const resetAt = useRef(0);
  const resetting = {
    get current() {
      return resetAt.current !== 0 && Date.now() - resetAt.current < 2000;
    },
    set current(v: boolean) {
      resetAt.current = v ? Date.now() : 0;
    },
  };
  const [, rerender] = useState(0);
  const update = useCallback((next: View[]) => {
    stack.current = next;
    rerender((n) => n + 1);
  }, []);

  useEffect(() => {
    history.replaceState({ depth: stack.current.length - 1 }, "");
    const onPop = (e: PopStateEvent) => {
      if (resetting.current) return; // a reset is driving history itself
      const target = typeof e.state?.depth === "number" ? (e.state.depth as number) : stack.current.length - 2;
      const current = stack.current.length - 1;
      if (target < current) {
        const keep = Math.max(0, target) + 1;
        forward.current = [...stack.current.slice(keep).reverse(), ...forward.current].slice(0, 20);
        update(stack.current.slice(0, keep));
      } else if (target > current) {
        const restored = forward.current.splice(0, target - current);
        if (restored.length) update([...stack.current, ...restored]);
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [update]);

  const push = useCallback(
    (view: View) => {
      forward.current = [];
      history.pushState({ depth: stack.current.length }, "");
      update([...stack.current, view]);
    },
    [update],
  );
  const replace = useCallback((view: View) => update([...stack.current.slice(0, -1), view]), [update]);
  // At the first screen there is nothing to go back to; history.back() there would leave the app.
  const back = useCallback(() => {
    if (stack.current.length > 1 && !resetting.current) history.back();
  }, []);
  const resetTo = useCallback(
    (view?: View) => {
      // Two callers can ask at once (a button, and the "computer was removed" safeguard). A second
      // history.go() would travel past the first entry and navigate away from the app, so only one runs.
      if (resetting.current) return;
      forward.current = [];
      const finish = () => {
        resetting.current = false;
        if (view) history.pushState({ depth: 1 }, "");
        update(view ? [ROOT, view] : [ROOT]);
      };
      const depth = stack.current.length - 1;
      if (depth <= 0) return finish();
      resetting.current = true;
      window.addEventListener("popstate", finish, { once: true });
      history.go(-depth);
    },
    [update],
  );
  const home = useCallback(() => resetTo(), [resetTo]);
  const goTab = useCallback(
    (view: View) => {
      if (resetting.current) return;
      forward.current = [];
      const depth = stack.current.length - 1;
      if (depth <= 0) return update([view]);
      resetting.current = true;
      window.addEventListener(
        "popstate",
        () => {
          resetting.current = false;
          update([view]);
        },
        { once: true },
      );
      history.go(-depth);
    },
    [update],
  );

  return useMemo(() => ({ view: stack.current[stack.current.length - 1]!, isRoot: stack.current.length === 1, push, replace, back, home, goTab, resetTo }), [stack.current, push, replace, back, home, goTab, resetTo]); // eslint-disable-line react-hooks/exhaustive-deps
}
